import React, { useState } from 'react';
import { useConfig } from '../config/ConfigContext';
import FeedbackForm from '../components/feedback/FeedbackForm';
import FeedbackSuccess from '../components/feedback/FeedbackSuccess';
import {
  submitFeedback,
  formatRatingsForSubmission,
  formatCommentsForSubmission,
  determinePrimarySection,
  FeedbackError
} from '../services/feedbackService';
import '../styles/feedback.css';

/**
 * Feedback page component
 * Handles the full feedback submission flow including form, loading, success, and error states
 * @returns {JSX.Element} FeedbackPage component
 */
const FeedbackPage = () => {
  const { config, loading: configLoading } = useConfig();

  // Page state
  const [state, setState] = useState('form'); // 'form' | 'loading' | 'success' | 'error'
  const [submissionResult, setSubmissionResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (formData) => {
    setState('loading');
    setError(null);

    try {
      // Format the data for submission
      const submissionData = {
        center_id: formData.center_id,
        center_name: formData.center_name,
        center_code: formData.center_code,
        user_name: formData.user_name || undefined,
        user_email: formData.user_email || undefined,
        section: determinePrimarySection(formData),
        ratings: formatRatingsForSubmission(formData.ratings),
        comments: formatCommentsForSubmission(formData.comments),
        improvement_suggestions: formData.improvement_suggestions?.trim() || undefined,
        additional_comments: formData.additional_comments?.trim() || undefined,
      };

      const result = await submitFeedback(submissionData);

      setSubmissionResult(result);
      setState('success');
    } catch (err) {
      console.error('[FeedbackPage] Submission error:', err);

      if (err instanceof FeedbackError) {
        setError({
          message: err.message,
          status: err.status,
          details: err.details,
        });
      } else {
        setError({
          message: 'An unexpected error occurred. Please try again.',
          status: 500,
          details: err.message,
        });
      }

      setState('error');
    }
  };

  const handleSubmitAnother = () => {
    setState('form');
    setSubmissionResult(null);
    setError(null);
  };

  const handleRetry = () => {
    setState('form');
    setError(null);
  };

  if (configLoading) {
    return (
      <div className="feedback-page">
        <div className="feedback-page-loading">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-page">
      <div className="feedback-page-container">
        {/* Header */}
        <header className="feedback-page-header">
          <h1>Share Your Feedback</h1>
          <p>
            Help us improve your experience at <strong>{config?.name || 'our center'}</strong>.
            Your feedback is valuable and will be reviewed by our team.
          </p>
        </header>

        {/* Form State */}
        {(state === 'form' || state === 'loading') && (
          <FeedbackForm
            onSubmit={handleSubmit}
            loading={state === 'loading'}
          />
        )}

        {/* Success State */}
        {state === 'success' && submissionResult && (
          <FeedbackSuccess
            submissionId={submissionResult.submission_id}
            metaIssue={submissionResult.meta_issue}
            projectIssue={submissionResult.project_issue}
            message={submissionResult.message}
            onSubmitAnother={handleSubmitAnother}
          />
        )}

        {/* Error State */}
        {state === 'error' && error && (
          <div className="feedback-error-state">
            <div className="feedback-error-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h2>Something Went Wrong</h2>

            <p className="feedback-error-message">
              {error.message}
            </p>

            {error.status === 0 && (
              <p className="feedback-error-hint">
                This might be a network issue. Please check your internet connection.
              </p>
            )}

            <div className="feedback-error-actions">
              <button
                type="button"
                className="feedback-retry-button"
                onClick={handleRetry}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
                Try Again
              </button>
            </div>

            {/* Debug info in development */}
            {process.env.NODE_ENV === 'development' && error.details && (
              <details className="feedback-error-details">
                <summary>Technical Details</summary>
                <pre>{error.details}</pre>
              </details>
            )}
          </div>
        )}

        {/* Privacy Note */}
        <footer className="feedback-page-footer">
          <p>
            Your feedback is stored securely and used to improve our services.
            Personal information is optional and will only be used to follow up on your feedback.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default FeedbackPage;
